/**
 * Дропдаун «колокольчика»: лента событий `GET /api/notifications` с вкладками
 * (notification-categories) + отметка прочтения `POST /api/notifications/read`
 * (capability notifications). Блок 7 (§3g): клик по уведомлению
 * с `relatedOrderId` ведёт к заказу — клиент к `/orders/{id}`, команда к `/team/orders/{id}`.
 */
import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Inbox } from 'lucide-react'
import {
  ApiError,
  notificationsApi,
  type Notification,
  type NotificationCategory,
} from '@/lib/api'
import { Card } from '@/design-system/components/layout/card'
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/design-system/components/primitives/tabs'
import { Button } from '@/design-system/components/primitives/button'
import { EmptyState } from '@/design-system/components/feedback/empty-state'
import { cn } from '@/lib/utils'
import { useAuth } from './auth-context'
import { isTeamRole } from './nav'

/**
 * Записи администраторам о состоянии отправки писем (email-daily-limit): сработал дневной
 * лимит, превышен месячный объём. Зеркало бэкенда — `notify.KindEmailLimitReached` /
 * `notify.KindEmailQuotaExceeded`.
 */
const EMAIL_LIMIT_KINDS: ReadonlySet<string> = new Set(['email_limit_reached', 'email_quota_exceeded'])

/**
 * Запись администраторам: у клиента заканчивается абонентское обслуживание по обучению
 * (training-subscription-expiry-notice). Зеркало бэкенда — `notify.KindTrainingSubscriptionExpiring`.
 */
const TRAINING_SUBSCRIPTION_EXPIRING_KIND = 'training_subscription_expiring'

/**
 * Запись сотруднику: его назначили менеджером клиента (team-visibility). Зеркало бэкенда —
 * `notify.EventClientAssigned`. «Вам назначен заказ» (`order_assigned`) несёт заказ и открывается
 * общим правилом.
 */
const CLIENT_ASSIGNED_KIND = 'client_assigned'

/**
 * Запись администраторам: появился клиент без менеджера по сопровождению (client-support-manager).
 * Зеркало бэкенда — `notify.EventClientWithoutSupportManager`. Открывает карточку клиента.
 */
const CLIENT_WITHOUT_SUPPORT_KIND = 'client_without_support_manager'

/**
 * Записи о подтверждении добавленного в каталог обучения (training-packages-approval):
 * `catalog_<program|package>_<proposal|approved|rejected>` — зеркало бэкенда
 * `notify.EventCatalog*`. Открывают вкладку каталога с фильтром: предложение — «На
 * подтверждении», решение — «Действующие» или «Отклонённые».
 */
const CATALOG_KIND = /^catalog_(program|package)_(proposal|approved|rejected)$/

export function catalogNotificationPath(kind: string): string | null {
  const m = CATALOG_KIND.exec(kind)
  if (!m) return null
  const tab = m[1] === 'package' ? 'packages' : 'programs'
  const show = m[2] === 'proposal' ? 'pending' : m[2] === 'approved' ? 'active' : 'rejected'
  return `/training/programs?tab=${tab}&show=${show}`
}

/**
 * Вкладки ленты (notification-categories; ТЗ п.9.8: «разделить сроки и состояние заказов»; ответ
 * заказчицы В25): клиент — «Все / Сроки / Заказы», команда — «Все / Заказы / Назначения и прочее».
 * Отбор — на сервере (`category`).
 */
type BellTab = 'all' | NotificationCategory

const CLIENT_TABS: { value: BellTab; label: string }[] = [
  { value: 'all', label: 'Все' },
  { value: 'deadlines', label: 'Сроки' },
  { value: 'orders', label: 'Заказы' },
]

const TEAM_TABS: { value: BellTab; label: string }[] = [
  { value: 'all', label: 'Все' },
  { value: 'orders', label: 'Заказы' },
  { value: 'other', label: 'Назначения и прочее' },
]

function formatTime(iso: string): string {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return ''
  return d.toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric' })
}

export interface NotificationDropdownProps {
  /** Закрыть дропдаун. */
  onClose: () => void
  /** Вызывается после изменения прочитанности — родитель обновляет счётчик в шапке. */
  onChanged: () => void
}

export function NotificationDropdown({ onClose, onChanged }: NotificationDropdownProps) {
  const [items, setItems] = useState<Notification[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const navigate = useNavigate()
  const { context } = useAuth()
  const role = context?.user.role
  const tabs = role && isTeamRole(role) ? TEAM_TABS : CLIENT_TABS
  const [tab, setTab] = useState<BellTab>('all')

  useEffect(() => {
    // Отменяем запрос при закрытии дропдауна и смене вкладки: ответ прежней вкладки не должен
    // перезаписать ленту.
    const controller = new AbortController()
    setLoading(true)
    setError(null)
    notificationsApi
      .list({ limit: 20, category: tab === 'all' ? undefined : tab }, controller.signal)
      .then((res) => {
        if (!controller.signal.aborted) setItems(res.items)
      })
      .catch((err) => {
        // AbortError при отмене — ожидаемо, не показываем как ошибку.
        if (controller.signal.aborted) return
        setError(err instanceof ApiError ? err.message : 'Не удалось загрузить уведомления')
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false)
      })
    return () => controller.abort()
  }, [tab])

  // Закрытие по Escape (как у нативных всплывающих меню).
  useEffect(() => {
    function onKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [onClose])

  const hasUnread = items.some((n) => !n.read)

  async function markRead(id: string) {
    setItems((prev) => prev.map((n) => (n.id === id ? { ...n, read: true } : n)))
    try {
      await notificationsApi.read({ ids: [id] })
      onChanged()
    } catch {
      // не откатываем оптимистично — счётчик подтянется при следующем refresh
    }
  }

  async function markAll() {
    setItems((prev) => prev.map((n) => ({ ...n, read: true })))
    try {
      // Во вкладке — только её записи (notification-categories).
      await notificationsApi.read({ all: true, category: tab === 'all' ? undefined : tab })
      onChanged()
    } catch {
      /* как выше */
    }
  }

  /** Путь к заказу по роли: команда → портал команды, клиент → свой скоуп (§3g). */
  function orderPath(orderId: string): string {
    return role && isTeamRole(role) ? `/team/orders/${orderId}` : `/orders/${orderId}`
  }

  /**
   * Клик по уведомлению: отметить прочитанным; при наличии заказа — перейти к нему и закрыть ленту.
   * «Опрос по экологии пройден» (ecology-survey) ведёт команду во вкладку «Экология» карточки клиента.
   * Записи о дневном лимите и месячном объёме писем (email-daily-limit) ведут администратора к
   * счётчику — на страницу «Уведомления». «Абонентское обслуживание по обучению заканчивается»
   * (training-subscription-expiry-notice) открывает карточку клиента на вкладке «Реквизиты».
   * Предложения и решения по каталогу обучения (training-packages-approval) открывают каталог.
   * Записи о вопросах клиентов (client-questions) открывают вопрос.
   */
  function handleSelect(n: Notification) {
    if (!n.read) void markRead(n.id)
    if (n.relatedQuestionId) {
      // Вопрос клиента (client-questions) — сам вопрос, даже если он задан из заказа.
      navigate(
        role && isTeamRole(role)
          ? `/team/questions/${n.relatedQuestionId}`
          : `/questions/${n.relatedQuestionId}`,
      )
      onClose()
    } else if (n.relatedOrderId) {
      navigate(orderPath(n.relatedOrderId))
      onClose()
    } else if (n.kind === 'eco_survey_completed' && n.relatedClientId && role && isTeamRole(role)) {
      navigate(`/team/clients/${n.relatedClientId}?tab=ecology`)
      onClose()
    } else if (
      n.kind === TRAINING_SUBSCRIPTION_EXPIRING_KIND &&
      n.relatedClientId &&
      role &&
      isTeamRole(role)
    ) {
      // Срок договора правится во вкладке «Реквизиты» карточки клиента.
      navigate(`/team/clients/${n.relatedClientId}?tab=profile`)
      onClose()
    } else if (n.kind === CLIENT_WITHOUT_SUPPORT_KIND && !n.relatedClientId && role && isTeamRole(role)) {
      // Несколько клиентов остались без сопровождения (передача дел «Никому») — список клиентов.
      navigate('/team/clients')
      onClose()
    } else if (
      (n.kind === CLIENT_ASSIGNED_KIND || n.kind === CLIENT_WITHOUT_SUPPORT_KIND) &&
      n.relatedClientId &&
      role &&
      isTeamRole(role)
    ) {
      // «Вам назначен клиент» (team-visibility) — карточка клиента.
      navigate(`/team/clients/${n.relatedClientId}`)
      onClose()
    } else if (EMAIL_LIMIT_KINDS.has(n.kind) && role === 'admin') {
      navigate('/team/notifications')
      onClose()
    } else if (catalogNotificationPath(n.kind) && role && isTeamRole(role)) {
      navigate(catalogNotificationPath(n.kind) as string)
      onClose()
    }
  }

  return (
    <>
      {/* Перехват клика вне дропдауна для закрытия. */}
      <button
        type="button"
        aria-label="Закрыть уведомления"
        className="fixed inset-0 z-sticky cursor-default bg-transparent"
        onClick={onClose}
      />
      <div className="absolute right-2 top-topbar z-sticky w-96 max-w-narrow">
        <Card
          variant="elevated"
          header={
            <div className="flex items-center justify-between">
              <span>Уведомления</span>
              {hasUnread ? (
                <Button size="sm" variant="secondary" onClick={markAll}>
                  Прочитать все
                </Button>
              ) : null}
            </div>
          }
          className="p-0"
        >
          <Tabs value={tab} onValueChange={(v) => setTab(v as BellTab)}>
            <TabsList aria-label="Какие уведомления показать" className="mb-1">
              {tabs.map((t) => (
                <TabsTrigger key={t.value} value={t.value}>
                  {t.label}
                </TabsTrigger>
              ))}
            </TabsList>
            <TabsContent value={tab} className="pt-0">
              {loading ? (
                <p className="px-1 py-6 text-center text-txt-secondary">Загрузка…</p>
              ) : error ? (
                <p className="px-1 py-6 text-center text-red">{error}</p>
              ) : items.length === 0 ? (
                <EmptyState
                  icon={Inbox}
                  title={tab === 'all' ? 'Пока нет уведомлений' : 'В этой вкладке уведомлений нет'}
                  className="py-10"
                />
              ) : (
                <ul className="-mx-1">
                  {items.map((n) => (
                    <li key={n.id}>
                      <button
                        type="button"
                        onClick={() => handleSelect(n)}
                        className={cn(
                          'flex w-full items-start gap-3 border-b border-line px-1 py-3 text-left outline-none transition-colors last:border-b-0',
                          'hover:bg-hover focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-accent',
                        )}
                      >
                        <span
                          aria-hidden
                          className={cn(
                            'mt-1.5 size-2 shrink-0 rounded-round',
                            n.read ? 'bg-transparent' : 'bg-accent',
                          )}
                        />
                        <span className="min-w-0 flex-1">
                          <span
                            className={cn('block', n.read ? 'text-txt' : 'font-semibold text-txt')}
                          >
                            {n.title}
                          </span>
                          {n.body ? (
                            <span className="block text-12 text-txt-secondary">{n.body}</span>
                          ) : null}
                          <span className="mt-0.5 block text-12 text-txt-tertiary">
                            {formatTime(n.createdAt)}
                          </span>
                        </span>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </TabsContent>
          </Tabs>
        </Card>
      </div>
    </>
  )
}
